import React from "react";
import { motion } from "framer-motion";
import { FaGraduationCap, FaSchool, FaUniversity } from "react-icons/fa";

const education = [
  {
    degree: "Bachelor of Technology (B.Tech) Electronics And Telecommunication",
    institution: "IIIT Bhubhaneshwar",
    duration: "2023 – 2027",
    score: "CGPA: 8.5*",
    icon: <FaUniversity className="text-2xl" />,
    color: "text-cyan-400",
    glowColor: "from-cyan-400 to-blue-500",
    bgGradient: "from-cyan-900/20 to-blue-900/10",
    borderGradient: "from-cyan-400 to-blue-500"
  },
  {
    degree: "Higher Secondary (Class 12)",
    institution: "Kendriya Vidyalaya SaltLake",
    duration: "2021 – 2023",
    score: "Percentage: 92%",
    icon: <FaSchool className="text-2xl" />,
    color: "text-pink-400",
    glowColor: "from-pink-400 to-rose-500",
    bgGradient: "from-pink-900/20 to-rose-900/10",
    borderGradient: "from-pink-400 to-rose-500"
  },
  {
    degree: "Secondary (Class 10)",
    institution: "Kendriya Vidyalaya SaltLake",
    duration: "2020 – 2021",
    score: "Percentage: 95%",
    icon: <FaGraduationCap className="text-2xl" />,
    color: "text-purple-400",
    glowColor: "from-purple-400 to-indigo-500",
    bgGradient: "from-purple-900/20 to-indigo-900/10",
    borderGradient: "from-purple-400 to-indigo-500"
  },
];

export default function Education() {
  return (
    <section id="education" className="relative text-white py-24 px-6 overflow-hidden ">
      <div className="max-w-4xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          viewport={{ once: true, margin: "-50px" }}
          className="text-center mb-16"
        >
          <motion.h2
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-5xl md:text-6xl font-bold mb-6"
          >
            <span className="text-transparent  bg-clip-text bg-gradient-to-r from-neon-cyan to-neon-purple">
              Education
            </span>
          </motion.h2>
          <motion.div
            initial={{ width: 0 }}
            whileInView={{ width: 120 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            viewport={{ once: true }}
            className="h-1 bg-gradient-to-r from-cyan-400 to-purple-400 mx-auto rounded-full"
          ></motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            viewport={{ once: true }}
            className="text-gray-400 mt-6 text-lg max-w-2xl mx-auto"
          >
            My academic journey through the years
          </motion.p>
        </motion.div>

        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-6 md:left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-cyan-400/50 via-purple-400/50 to-pink-400/50"></div>
          
          <div className="space-y-8">
            {education.map((edu, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ 
                  delay: index * 0.15, 
                  duration: 0.6,
                  ease: "easeOut"
                }}
                viewport={{ once: true, margin: "-50px" }}
                className="group relative"
              >
                {/* Timeline dot */}
                <div className={`absolute left-6 md:left-8 top-8 transform -translate-x-1/2 w-4 h-4 rounded-full border-4 border-white bg-gradient-to-r ${edu.glowColor} shadow-lg shadow-current/50 z-20`}></div>
                
                <div className="ml-12 md:ml-16">
                  <div className={`
                    relative overflow-hidden rounded-2xl 
                    bg-gradient-to-br ${edu.bgGradient}
                    border border-white/10 
                    backdrop-blur-xl
                    transition-all duration-500 ease-out
                    group-hover:scale-[1.02] 
                    group-hover:border-white/20
                    before:absolute before:inset-0 
                    before:bg-gradient-to-r ${edu.glowColor} 
                    before:opacity-0 before:transition-opacity 
                    before:duration-500 
                    group-hover:before:opacity-10
                  `}>
                    {/* Main content */}
                    <div className="relative p-6 md:p-8 flex flex-col md:flex-row gap-6 items-start">
                      {/* Icon container */}
                      <div className={`
                        flex items-center justify-center 
                        w-14 h-14 rounded-2xl
                        bg-white/5 border border-white/10
                        transition-all duration-300
                        group-hover:scale-110 
                        group-hover:bg-white/10
                        group-hover:border-white/20
                        ${edu.color}
                      `}>
                        {edu.icon}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 space-y-4">
                        <div>
                          <h3 className={`text-xl md:text-2xl font-bold mb-2 leading-tight ${edu.color}`}>
                            {edu.degree}
                          </h3>
                          <p className="text-gray-300 text-lg font-medium mb-1">
                            {edu.institution}
                          </p>
                        </div>
                        
                        <div className="flex flex-col sm:flex-row sm:items-center gap-4 text-sm">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse"></div>
                              <span className="text-gray-400">{edu.duration}</span>
                            </div>
                            <div className="w-1 h-1 rounded-full bg-white/30"></div>
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-pink-400 animate-pulse"></div>
                              <span className={`font-semibold ${edu.color}`}>{edu.score}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Hover glow effect */}
                    <div className={`absolute inset-0 rounded-2xl bg-gradient-to-r ${edu.glowColor} opacity-0 group-hover:opacity-5 transition-opacity duration-500 pointer-events-none`}></div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Floating elements */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.9 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <div className="inline-flex items-center gap-2 text-gray-400 text-sm">
            <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse"></div>
            Continuing my learning journey
          </div>
        </motion.div>
      </div>

      <style jsx global>{`
        @keyframes float {
          0%, 100% { 
            transform: translateY(0px) rotate(0deg); 
          }
          33% { 
            transform: translateY(-20px) rotate(120deg); 
          }
          66% { 
            transform: translateY(10px) rotate(240deg); 
          }
        }
        
        @keyframes float-delayed {
          0%, 100% { 
            transform: translateY(-10px) rotate(45deg); 
          }
          50% { 
            transform: translateY(20px) rotate(135deg); 
          }
        }
        
        @keyframes pulse-slow {
          0%, 100% { 
            opacity: 0.05; 
            transform: scale(1);
          }
          50% { 
            opacity: 0.1; 
            transform: scale(1.1);
          }
        }
        
        .animate-float { 
          animation: float 15s ease-in-out infinite; 
        }
        
        .animate-float-delayed { 
          animation: float-delayed 12s ease-in-out infinite 1s; 
        }
        
        .animate-pulse-slow { 
          animation: pulse-slow 8s ease-in-out infinite; 
        }
      `}</style>
    </section>
  );
}